import { useEffect, useRef } from 'react';

const FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Fenêtre modale ou tiroir accessibles : à l'ouverture, le focus entre dans `ref`
 * (premier élément focalisable, sinon le conteneur) ; Tab / Maj+Tab restent à
 * l'intérieur ; Échap appelle `onEscape` ; à la fermeture, le focus revient sur
 * l'élément qui l'avait (bouton d'ouverture).
 */
export function useFocusTrap<T extends HTMLElement>(active: boolean, onEscape?: () => void) {
    const ref = useRef<T>(null);
    const onEscapeRef = useRef(onEscape);
    onEscapeRef.current = onEscape;

    useEffect(() => {
        if (!active) return;
        const container = ref.current;
        if (!container) return;
        const previous = document.activeElement as HTMLElement | null;
        const focusables = (): HTMLElement[] => (Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE)) as HTMLElement[])
            .filter((el) => el.offsetParent !== null || el === document.activeElement);

        const first = container.querySelector<HTMLElement>('[autofocus]') ?? focusables()[0];
        (first ?? container).focus();

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && onEscapeRef.current) {
                e.stopPropagation();
                onEscapeRef.current();
                return;
            }
            if (e.key !== 'Tab') return;
            const items = focusables();
            if (items.length === 0) { e.preventDefault(); container.focus(); return; }
            const firstEl = items[0];
            const lastEl = items[items.length - 1];
            if (e.shiftKey && (document.activeElement === firstEl || !container.contains(document.activeElement))) {
                e.preventDefault();
                lastEl.focus();
            } else if (!e.shiftKey && document.activeElement === lastEl) {
                e.preventDefault();
                firstEl.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            if (previous && document.contains(previous)) previous.focus();
        };
    }, [active]);

    return ref;
}
